import { useDidUpdate, useDisclosure } from '@mantine/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGlobalStore } from '../../../store/global-store/global-store';
import { useStrategyStore } from '../../../store/strategy-store/strategy-store';
import { setActivePanel } from '../../../store/strategy-store/strategy-store.actions';
import { Panel } from '../../../store/strategy-store/strategy-store.type';
import { baseWhite } from '../../../styles/design-tokens';
import { Button } from '../../../ui-library/button/button';
import { Icon } from '../../../ui-library/icon/icon';
import { toast } from '../../notifications';
import { StrategyEntityModal } from '../strategy-entity-modal/strategy-entity-modal';
import { StrategyView } from '../strategy-view';
import { useCreateGoalMutation, useGoalsQuery, useInitiativesQuery } from '../strategy.hooks';
import { StrategyEntityType } from '../strategy.type';
import { getGoalsConfig } from './goals.config';

/**
 * Goals strategy level view
 */
export function Goals() {
  const { t } = useTranslation();
  const goalsConfig = useMemo(() => getGoalsConfig(t), [t]);
  const queryClient = useQueryClient();
  const { initiativesQuery } = useInitiativesQuery();
  const { query: goalsQuery } = useGoalsQuery();
  const portfolio = useGlobalStore((state) => state.portfolio);
  const selectedGoal = useStrategyStore((state) => state.selectedGoal);
  const activePanel = useStrategyStore((state) => state.activePanel);

  const [opened, { open, close }] = useDisclosure(false);
  const [modalEntityType, setModalEntityType] = useState<StrategyEntityType>(StrategyEntityType.Goal);

  useEffect(() => {
    return () => {
      setActivePanel(Panel.Filters);
    };
  }, []);

  useDidUpdate(() => {
    if (portfolio) {
      setActivePanel(Panel.Filters);
    }
  }, [portfolio]);

  const createGoalMutation = useCreateGoalMutation({
    onSuccess: (data) => {
      goalsQuery.refetch();
      initiativesQuery.refetch();
      if (portfolio?.id) {
        queryClient.invalidateQueries({ queryKey: ['kpi-overview-data', portfolio.id] });
      }
      close();
      toast.success(`Strategic Goal "${data.name}" created successfully`);
    },
    onError: (error) => {
      toast.error(`Failed to create strategic goal: ${error instanceof Error ? error.message : 'Unknown error'}`);
    },
  });

  const addGoalButton = (
    <Button
      data-bf="strategy-btn-add-goal"
      leftSection={<Icon name="add_circle" size={16} color={baseWhite} />}
      variant="primary"
      radius="xl"
      onClick={() => {
        setModalEntityType(StrategyEntityType.Goal);
        open();
      }}
      size="sm"
    >
      Add New
    </Button>
  );

  const updatedConfig = {
    ...goalsConfig,
    renderers: {
      renderAddButton: () => addGoalButton,
    },
  };

  function handleSetShow(show: boolean): void {
    setActivePanel(show ? Panel.Filters : null);
  }

  return (
    <>
      <StrategyView
        config={updatedConfig}
        customShowSideSection={Boolean(selectedGoal)}
        customFilterShow={activePanel === Panel.Filters}
        customSetFilterShow={handleSetShow}
      />
      <StrategyEntityModal
        mode="create"
        entityType={modalEntityType}
        opened={opened}
        onClose={close}
        onSubmit={createGoalMutation.mutateAsync}
        isSubmitting={createGoalMutation.isPending}
      />
    </>
  );
}
